import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import GetCookie from "../../../../utils/GetCookie";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";
import PaginationControls from "../../../../utils/PaginationControls";
import authNavigate from "../../../../globalComponents/AuthNav";
import { useNavigate } from "react-router-dom";
import { PICO_COLORS, normalizePicoKey, buildTagOptionsFromPicoJson } from "../../../../utils/Picocolors";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import TagFilterPanel from "./TagFilterPanel";
import YearRangePicker from "../../../../utils/YearRangePicker";


/* A native <select> renders its list through the OS and ignores the app's
   styling, so this is a toggle button plus a floating card of options: light
   rows, the selected one filled blue, at most 15vh tall and scrollable.
   Closes on an outside click or Esc. */
const InlineSelect = ({ id, openId, setOpenId, value, options = [], placeholder = "Select", width, className = "" }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const controlled = typeof setOpenId === "function";
  const isOpen = controlled ? openId === id : localOpen;
  const setOpen = (next) => {
    if (controlled) setOpenId(next ? id : null);
    else setLocalOpen(next);
  };
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Bring the selected option into view inside the 15vh list (without
    // scrolling the filter panel itself).
    const list = listRef.current;
    const active = list?.querySelector('[data-selected="true"]');
    if (list && active) {
      list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.offsetHeight / 2;
    }

    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div ref={rootRef} className={`position-relative ${className}`} style={width ? { width } : undefined}>
      <button
        type="button"
        className="ev-btn-light-fill ev-btn-small ev-radius-xsmall w-100 d-flex align-items-center justify-content-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setOpen(!isOpen)}
      >
        <span className="text-truncate">{selected ? selected.label : placeholder}</span>
        <i className={`bi bi-chevron-${isOpen ? "up" : "down"} ms-1`}></i>
      </button>
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          className="position-absolute start-0 mt-1 p-2 bg-white border border-primary ev-radius-xsmall shadow-sm d-flex flex-column gap-1 scrollable-element"
          style={{
            top: "100%",
            zIndex: 1000,
            minWidth: "100%",
            width: "max-content",
            maxHeight: "20vh",
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : undefined}
                className={`${isSelected ? "ev-btn-blue-fill-round" : "ev-btn-light-fill"} ev-btn-small ev-radius-xsmall w-100 text-start text-nowrap flex-shrink-0`}
                onClick={() => { opt.onSelect?.(); setOpen(false); }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ADV_FIELD_OPTIONS = [
  { value: "", label: "Field" },
  { value: "title", label: "Title" },
  { value: "abstract", label: "Abstract" },
  { value: "author", label: "Author" },
  { value: "year", label: "Year" },
  { value: "population", label: "Population" },
  { value: "intervention", label: "Intervention" },
  { value: "comparison", label: "Comparator" },
  { value: "outcome", label: "Outcome" },
  { value: "study_design", label: "Study Design" },
  { value: "exclusion", label: "Exclusion" },
];

const ADV_OPERATOR_OPTIONS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Does not equal" },
  { value: "begins_with", label: "Starts with" },
  { value: "not_begins_with", label: "Does not start with" },
  { value: "ends_with", label: "Ends with" },
  { value: "not_ends_with", label: "Does not end with" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
  { value: "is_null", label: "Is null" },
  { value: "is_not_null", label: "Is not null" },
];

// De-dup tab filters
const MATCH_LEVEL_OPTIONS = [
  { value: "strong", label: "Strong" },
  { value: "possible", label: "Possible" },
  { value: "weak", label: "Weak" },
];

const DEDUP_STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "RESOLVED", label: "Resolved" },
];

// De-dup paper cards: thick accent bar on the left + thin outline.
// Green = paper that stays (kept / original), yellow = duplicate.
const DEDUP_CARD_COLORS = {
  KEPT: { strong: "#198754", light: "rgba(25, 135, 84, 0.35)" },
  DUPLICATE: { strong: "#f5d26a", light: "rgba(255, 193, 7, 0.5)" },
};

// Uses borderWidth / borderColor only (no `border` + `borderLeft` mix), so
// React can switch the colours on re-render without style warnings.
const dedupCardBorderStyle = ({ strong, light }) => ({
  borderStyle: "solid",
  borderWidth: "1px 1px 1px 6px",
  borderColor: `${light} ${light} ${light} ${strong}`,
});

const sameIdSet = (a, b) => {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

const sortStudyIds = (ids) =>
  [...ids].sort((a, b) => {
    const na = Number(a), nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });

const formatResolvedAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const previewSearchQuery = (query, max = 55) => {
  if (!query) return "";
  return query.length > max ? `${query.slice(0, max).trimEnd()}…` : query;
};

const AbstractsList = ({ fabricId, picoJson = null, tagOptions = [], onBackToProjects }) => {
    const [activeTab, setActiveTab] = useState("DEFAULT");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [defaultData, setDefaultData] = useState(null);
    const [dedupData, setDedupData] = useState(null);

    const [selectedAbstractId, setSelectedAbstractId] = useState(null);
    const [expandedAbstractIds, setExpandedAbstractIds] = useState(() => new Set());

    const [expandedDedupItemIds, setExpandedDedupItemIds] = useState(() => new Set());
    // Unsaved selections only: group_key -> Set(paper ids). A group is in
    // here only while it differs from what is saved on the server.
    const [selectedByGroup, setSelectedByGroup] = useState({});
    // Saved decision per group (from the server): group_key -> { kept: [paper ids], status }
    const [persistedByGroup, setPersistedByGroup] = useState({});
    // De-dup filters
    const [matchLevels, setMatchLevels] = useState([]);   // [] = all levels
    const [dedupStatus, setDedupStatus] = useState("ALL"); // ALL | PENDING | RESOLVED
    const [dedupSectionOpen, setDedupSectionOpen] = useState(true);

    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const [section1Open, setSection1Open] = useState(false);
    const [section2Open, setSection2Open] = useState(false);
    const [section3Open, setSection3Open] = useState(false);
    const [advancedEnabled, setAdvancedEnabled] = useState(false);
    const [advConditions, setAdvConditions] = useState([
        { id: 1, category: "", filterOp: "contains", connector: "AND", value: "" },
    ]);
    // Which advanced-filter dropdown is expanded, keyed "<ruleId>:<which>".
    const [advOpenDropdown, setAdvOpenDropdown] = useState(null);
    // Paper type filter: ALL | BIBLIOGRAPHIC (papers added via Full-Text QC → Add Paper)
    const [paperType, setPaperType] = useState("ALL");
    const [bibliographicIds, setBibliographicIds] = useState(() => new Set());
    const activeFilterCount =
        (paperType === "BIBLIOGRAPHIC" ? 1 : 0) +
        (searchQuery ? 1 : 0) +
        (dateFrom ? 1 : 0) +
        (dateTo ? 1 : 0) +
        selectedTagIds.length +
        (advancedEnabled ? advConditions.filter(c => c.category || c.value).length : 0) +
        (activeTab === "DEDUP" ? matchLevels.length + (dedupStatus !== "ALL" ? 1 : 0) : 0);

    const clearAllFilters = () => {
        setSearchInput("");
        setSearchQuery("");
        setDateFrom("");
        setDateTo("");
        setSelectedTagIds([]);
        setPaperType("ALL");
        setMatchLevels([]);
        setDedupStatus("ALL");
        setAdvancedEnabled(false);
        setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }]);
        setPage(1);
    };

    const addAdvCondition = () =>
        setAdvConditions((prev) => [
            ...prev,
            { id: Date.now(), category: "", filterOp: "contains", connector: "AND", value: "" },
        ]);
    const removeAdvCondition = (id) =>
        setAdvConditions((prev) => prev.filter((c) => c.id !== id));
    const updateAdvCondition = (id, field, val) =>
        setAdvConditions((prev) =>
            prev.map((c) => (c.id === id ? { ...c, [field]: val } : c))
        );

    const buildAdvancedFiltersPayload = () =>
        advancedEnabled && advConditions.some(c => c.category || c.value)
            ? { rules: advConditions.filter(c => c.category || c.value).map(c => ({ field: c.category, operator: c.filterOp || "contains", value: c.value, connector: c.connector || "AND" })) }
            : null;

    const toggleMatchLevel = (level) => {
        setMatchLevels((prev) =>
            prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
        );
        setPage(1);
    };

    const matchLevelLabel = MATCH_LEVEL_OPTIONS
        .filter((o) => matchLevels.includes(o.value))
        .map((o) => o.label)
        .join(", ");


    const API_BASE_URL = "/pceapi/slr";
    const headers = useMemo(
        () => ({
            Authorization: GetCookie("access_token"),
        }),
        [],
    );

    const navigate = useNavigate();

    // ── QC lock ──────────────────────────────────────────────────────────
    // After Abstract QC is marked complete only the project creator may
    // resolve duplicates (the backend refuses everyone else).
    const [dedupLocked, setDedupLocked] = useState(false);
    useEffect(() => {
        setDedupLocked(false);
        if (!fabricId) return undefined;
        let cancelled = false;
        axios.post(`${API_BASE_URL}/fabric-details`, { slr_fabric_id: fabricId }, { headers })
            .then((res) => {
                if (cancelled || res.data?.status !== "success") return;
                const d = res.data.data;
                const qcDone = d?.abstract_screening?.statuses?.qc_status === "COMPLETED";
                const isOwner = Boolean(d?.user_reviewer_status?.is_owner);
                setDedupLocked(qcDone && !isOwner);
            })
            .catch(() => { /* backend still enforces the lock */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fabricId]);
    const DEDUP_LOCK_MESSAGE = "Abstract QC is complete. Only the project admin can resolve duplicates now.";

    // Group keys are study_ids, so they are only unique within one project
    useEffect(() => {
        setSelectedByGroup({});
        setPersistedByGroup({});
    }, [fabricId]);

    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");

    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");

    const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
    const [popupSuccessMessage, setPopupSuccessMessage] = useState("");
    const [showAutoResolveConfirm, setShowAutoResolveConfirm] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [pendingSavePayload, setPendingSavePayload] = useState(null);
    const [showSkippedPapers, setShowSkippedPapers] = useState(false);

    //ref to track if the error was a "setup required" error
    const isSetupErrorRef = useRef(false);

    //on close, call onBackToProjects if it was a setup error
    const closeErrorPopup = () => {
        setIsErrorPopupVisible(false);
        if (isSetupErrorRef.current) {
            isSetupErrorRef.current = false;
            onBackToProjects?.();
        }
    };

    const closeSuccessPopup = async () => {
        setIsSuccessPopupVisible(false);
        await fetchData({ message: "Refreshing abstracts list..." });
    };

    const showError = (msg) => {
        setPopupErrorMessage(msg || "Something went wrong");
        setIsErrorPopupVisible(true);
    };

    const showSuccess = (msg) => {
        setPopupSuccessMessage(msg || "Action completed successfully");
        setIsSuccessPopupVisible(true);
    };

    const handleNotAuthorized = () => {
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
        authNavigate("/login", "iam", navigate);
    };

    const totalCount = useMemo(() => {
        if (activeTab === "DEFAULT") {
            return defaultData?.counts?.total || defaultData?.pagination?.total || 0;
        }
        // Every abstract in the matching groups (originals + duplicates)
        return dedupData?.counts?.total_abstracts || 0;
    }, [activeTab, defaultData, dedupData]);

    const totalPages = useMemo(() => {
        if (activeTab === "DEFAULT") {
            return defaultData?.pagination?.total_pages ||
                Math.max(1, Math.ceil((defaultData?.counts?.total || 0) / Math.max(1, pageSize)));
        }
        return dedupData?.pagination?.total_pages ||
            Math.max(1, Math.ceil((dedupData?.counts?.total_groups || 0) / Math.max(1, pageSize)));
    }, [activeTab, defaultData, dedupData, pageSize]);

    const showingCount = useMemo(() => {
        if (activeTab === "DEFAULT") {
            return defaultData?.abstracts?.length || 0;
        }
        const groups = dedupData?.duplicate_groups || [];
        return groups.reduce((acc, g) => acc + 1 + ((g?.duplicates || []).length || 0), 0);
    }, [activeTab, defaultData, dedupData]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [totalPages]);

    // selectedByGroup only holds groups that differ from the saved decision
    const showSaveSelection = useMemo(
        () => Object.keys(selectedByGroup || {}).length > 0,
        [selectedByGroup],
    );

    // Unsaved selection if any, otherwise the saved keepers
    const getGroupSelection = (groupKey) =>
        selectedByGroup[groupKey] || new Set(persistedByGroup[groupKey]?.kept || []);

    // Resolved earlier, but a newly imported paper joined the group: the
    // saved keepers are pre-selected and the group needs one more Save.
    const needsReview = (groupKey) => {
        const p = persistedByGroup[groupKey];
        return Boolean(p && p.status === "PENDING" && (p.kept || []).length > 0);
    };

    const originalIdsSet = useMemo(() => {
        const groups = dedupData?.duplicate_groups || [];
        const ids = groups.map((g) => g?.original?.id).filter(Boolean);
        return new Set(ids);
    }, [dedupData]);

    const migrationWarnings = useMemo(() => {
        if (activeTab !== "DEFAULT") return [];
        return defaultData?.migration_warnings || [];
    }, [activeTab, defaultData]);

    const skippedPapersCount = useMemo(
        () => migrationWarnings.reduce((acc, w) => acc + (w?.skipped_count || 0), 0),
        [migrationWarnings],
    );

    const effectiveTagOptions = useMemo(() => {
        const input = Array.isArray(tagOptions) ? tagOptions : [];
        if (input.length > 0) return input;

        const derived = buildTagOptionsFromPicoJson(picoJson);
        return derived;
    }, [tagOptions, picoJson]);

    const fetchData = async ({ message } = {}) => {
        if (!fabricId) return;

        setIsProcessingPopupVisible(true);
        setProcessingMessage(message || "Loading abstracts list...");

        setLoading(true);
        try {
            const payload = {
                slr_fabric_id: fabricId,
                view: activeTab === "DEFAULT" ? "default" : "deduplication",
                page,
                size: pageSize,
                search: searchQuery ? searchQuery : null,
                start_year: dateFrom || null,
                end_year: dateTo || null,
                selected_tags: selectedTagIds?.length ? selectedTagIds : null,
                paper_type: paperType,
                advanced_filters: buildAdvancedFiltersPayload(),
                match_levels: activeTab === "DEDUP" && matchLevels.length ? matchLevels : null,
                dedup_status: activeTab === "DEDUP" ? dedupStatus : null,
            };

            const res = await axios.post(`${API_BASE_URL}/abstract-screening/list`, payload, {
                headers,
            });

            const data = res?.data;

            if (data?.status === "NotAuthorized") {
                handleNotAuthorized();
                return;
            }

            // set ref before showing error so closeErrorPopup can redirect.
            // Driven by an explicit backend flag — matching on the message text
            // broke silently whenever a message was reworded, and bounced the
            // user out of the project for any unrelated error that happened to
            // contain the word.
            if (data?.status === "failed") {
                isSetupErrorRef.current = data?.setup_required === true;
                showError(data?.message || "Failed to load abstracts list");
                return;
            }

            if (data?.status === "success") {
                // Handle BOTH response structures:
                // Default: { status, data: { view, abstracts, counts }, pagination }
                // Deduplication: { status, view, duplicate_groups, total_groups, total_duplicates, page, size }

                const isFlat = !data.data && data.view;  // Flat structure has 'view' at root level

                if (isFlat) {
                    // FLAT structure (deduplication response)
                    if (data.view === "deduplication") {
                        if (typeof data.dedup_locked === "boolean") {
                            setDedupLocked(data.dedup_locked);
                        }
                        const groups = data.duplicate_groups || [];
                        const c = data.counts || {};
                        const totalGroups = c.total_groups ?? data.total_groups ?? 0;
                        setDedupData({
                            duplicate_groups: groups,
                            counts: {
                                total_abstracts: c.total_abstracts ?? data.total_abstracts ?? 0,
                                total_groups: totalGroups,
                                total_duplicates: c.total_duplicates ?? data.total_duplicates ?? 0,
                                pending_groups: c.pending_groups ?? 0,
                                resolved_groups: c.resolved_groups ?? 0,
                                levels: c.levels || {},
                            },
                            pagination: {
                                page: data.page || 1,
                                size: data.size || 25,
                                total: totalGroups,
                                total_pages: data.total_pages || Math.ceil(totalGroups / (data.size || 25)) || 1
                            }
                        });
                        // Saved keepers drive the "Kept" / "Duplicate of" tags
                        setPersistedByGroup((prev) => {
                            const next = { ...prev };
                            groups.forEach((g) => {
                                next[g.group_key] = { kept: g.kept_ids || [], status: g.status };
                            });
                            return next;
                        });
                        // Groups re-opened by a new paper: pre-select the saved
                        // keepers so one Save resolves the new paper too.
                        setSelectedByGroup((prev) => {
                            let changed = false;
                            const next = { ...prev };
                            groups.forEach((g) => {
                                if (g.status === "PENDING" && (g.kept_ids || []).length && !next[g.group_key]) {
                                    next[g.group_key] = new Set(g.kept_ids);
                                    changed = true;
                                }
                            });
                            return changed ? next : prev;
                        });
                    } else {
                        setDefaultData({
                            abstracts: data.abstracts || [],
                            counts: { total: data.total_count || 0 },
                            migration_warnings: data.migration_warnings || [],
                            pagination: {
                                page: data.page || 1,
                                size: data.size || 25,
                                total: data.total_count || 0,
                                total_pages: data.total_pages || 1
                            }
                        });
                    }
                } else {
                    // NESTED structure (default response)
                    const viewData = data.data || {};
                    const paginationData = data.pagination || {};

                    if (viewData.view === "default") {
                        setDefaultData({
                            abstracts: viewData.abstracts || [],
                            counts: viewData.counts || {},
                            migration_warnings: viewData.migration_warnings || [],
                            pagination: paginationData
                        });
                    } else {
                        setDedupData({
                            duplicate_groups: viewData.duplicate_groups || [],
                            counts: viewData.counts || {},
                            pagination: paginationData
                        });
                    }
                }
            } else {
                showError("Failed to load abstracts list");
            }
        } catch (err) {
            console.error("Failed to fetch abstracts list", err);
            showError(err?.message || "Failed to load abstracts list");
        } finally {
            setLoading(false);
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    useEffect(() => {
        // Without this guard the first mount can fire a request with
        // slr_fabric_id undefined — the project details that supply it arrive
        // asynchronously. The sibling effect below already guards the same way.
        if (!fabricId) return;
        fetchData({ message: "Loading abstracts list..." });
    }, [fabricId, activeTab, page, pageSize, searchQuery, dateFrom, dateTo, selectedTagIds, paperType, matchLevels, dedupStatus]);

    // "Bibliographic" badge: study_ids of papers added via Add Paper
    useEffect(() => {
        if (!fabricId) return;
        let cancelled = false;
        axios.post(`${API_BASE_URL}/qc/bibliographic-study-ids`, { slr_fabric_config_id: fabricId }, { headers })
            .then((res) => {
                if (!cancelled && res.data?.status === "success") {
                    setBibliographicIds(new Set((res.data.study_ids || []).map(String)));
                }
            })
            .catch((err) => console.error("Bibliographic ids fetch failed:", err));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fabricId]);

    // Filter is hidden when there are no bibliographic papers — never leave it stuck on BIBLIOGRAPHIC
    useEffect(() => {
        if (bibliographicIds.size === 0 && paperType === "BIBLIOGRAPHIC") setPaperType("ALL");
    }, [bibliographicIds, paperType]);

    const handlePrev = () => {
        if (page > 1) setPage((p) => p - 1);
    };

    const handleNext = () => {
        if (page < totalPages) setPage((p) => p + 1);
    };

    const handleSearch = () => {
        setSearchQuery(searchInput.trim());
        setPage(1);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const toggleExpandedDefault = (id) => {
        setExpandedAbstractIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleExpandedDedup = (id) => {
        setExpandedDedupItemIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectDedupAbstract = (groupKey, itemId) => {
        if (dedupLocked) return;
        setSelectedByGroup((prev) => {
            const saved = new Set(persistedByGroup[groupKey]?.kept || []);
            const set = new Set(prev[groupKey] || saved);
            if (set.has(itemId)) set.delete(itemId);
            else set.add(itemId);

            const next = { ...prev };
            // Back to the saved decision -> nothing to save for this group
            if (sameIdSet(set, saved) && !needsReview(groupKey)) delete next[groupKey];
            else next[groupKey] = set;

            return next;
        });
    };
    const handleSaveSelection = () => {
        if (!fabricId) return;
        if (dedupLocked) { showError(DEDUP_LOCK_MESSAGE); return; }

        const exclusionGroups = [];
        const titleByKey = Object.fromEntries(
            (dedupData?.duplicate_groups || []).map((g) => [g.group_key, g.group_title]),
        );

        // Changed groups only — they can be on other pages too
        for (const [groupKey, selectedSet] of Object.entries(selectedByGroup)) {
            if (!selectedSet || selectedSet.size === 0) {
                const title = titleByKey[groupKey];
                showError(`Must select at least one paper to keep in group ${title ? `"${title}"` : groupKey}`);
                return;
            }
            exclusionGroups.push({
                group_key: groupKey,
                selected_study_ids: Array.from(selectedSet),
            });
        }

        if (exclusionGroups.length === 0) {
            showError("Please select papers to keep from at least one group");
            return;
        }

        setPendingSavePayload(exclusionGroups);
        setShowSaveConfirm(true);
    };

    const confirmSaveSelection = async () => {
      setShowSaveConfirm(false);

      if (!pendingSavePayload || !fabricId) return;

      setIsProcessingPopupVisible(true);
      setProcessingMessage("Saving duplicate resolution...");

      try {
        const res = await axios.post(
          `${API_BASE_URL}/abstract-screening/save-duplicate-exclusions`,
          {
            slr_fabric_id: fabricId,
            exclusion_groups: pendingSavePayload,
          },
          { headers },
        );

        const data = res?.data;

        if (data?.status === "NotAuthorized") {
          handleNotAuthorized();
          return;
        }

        if (data?.status === "success") {
          setSelectedByGroup({});
          setPendingSavePayload(null);
          showSuccess(
            data?.message || "Duplicate groups resolved successfully",
          );
        } else {
          showError(data?.message || "Failed to save selection");
        }
      } catch (err) {
        showError(err?.message || "Failed to save selection");
      } finally {
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
      }
    };
    

    const confirmAutoResolveExact = async () => {
        if (!fabricId) return;
        if (dedupLocked) { setShowAutoResolveConfirm(false); showError(DEDUP_LOCK_MESSAGE); return; }
        if (!matchLevels.length) { setShowAutoResolveConfirm(false); showError("Select at least one match level in the filters to auto-resolve."); return; }

        setShowAutoResolveConfirm(false);

        setIsProcessingPopupVisible(true);
        setProcessingMessage("Auto-resolving duplicate groups...");


        try {
            // Same filters as the list: only the groups the user is looking at
            const res = await axios.post(
                `${API_BASE_URL}/abstract-screening/auto-resolve-duplicates`,
                {
                    slr_fabric_id: fabricId,
                    match_levels: matchLevels,
                    search: searchQuery ? searchQuery : null,
                    start_year: dateFrom || null,
                    end_year: dateTo || null,
                    selected_tags: selectedTagIds?.length ? selectedTagIds : null,
                    advanced_filters: buildAdvancedFiltersPayload(),
                },
                { headers },
            );

            const data = res?.data;

            if (data?.status === "NotAuthorized") {
                handleNotAuthorized();
                return;
            }

            if (data?.status === "failed") {
                showError(data?.message || "Failed to auto-resolve duplicates");
                return;
            }

            if (data?.status === "success") {
                setSelectedByGroup({});
                showSuccess(data?.message || "Duplicate groups auto-resolved");
            } else {
                showError("Failed to auto-resolve duplicates");
            }
        } catch (err) {
            console.error("Failed to auto-resolve duplicates", err);
            showError(err?.message || "Failed to auto-resolve duplicates");
        } finally {
            setIsProcessingPopupVisible(false);
            setProcessingMessage("");
        }
    };

    const toggleTag = (tagId) => {
        if (!tagId) return;
        setSelectedTagIds((prev) => {
            const next = new Set(prev || []);
            if (next.has(tagId)) next.delete(tagId);
            else next.add(tagId);
            return Array.from(next);
        });
        setPage(1);
    };

    const isValidField = (value) => {
        if (value === null || value === undefined) return false;
        const str = String(value).trim();
        return str !== "" && str.toLowerCase() !== "null" && str.toLowerCase() !== "none" && str !== "0" && str !== "0.0";
    };

    const MatchPill = ({ type, level }) => {
        if (type === "ORIGINAL") {
            return (
                <div className="ev-message-card6 ev-radius-large ev-bold-med py-1 ev-btn-xsmall px-3 text-nowrap">
                    Original
                </div>
            );
        }
        // 'exact' is the old top level from earlier imports -> shown as Strong.
        const levelConfig = {
            strong:   { label: "Strong Match",   cls: "ev-alert-card-success" },
            exact:    { label: "Strong Match",   cls: "ev-alert-card-success" },
            possible: { label: "Possible Match", cls: "ev-alert-card-warn"    },
            weak:     { label: "Weak Match",     cls: "ev-message-card3"      },
        };
        const { label, cls } = levelConfig[level] || levelConfig.weak;
        return (
            <div className={`${cls} ev-radius-large ev-bold-med py-1 ev-btn-xsmall px-3 text-nowrap`}>
                {label}
            </div>
        );
    };

    return (
      <div className="h-100 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex w-70 gap-4 align-items-center">
            <p className="ev-med-p-blue ev-bold-med mb-0 ms-2">
              Abstracts List
            </p>
            <div className="position-relative w-55">
              <input
                type="text"
                className="form-control search-input ms-0"
                placeholder="Search by Study Id, Title, Abstract or Author Name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
              <button
                className="ev-btn-lightblue-fill-round search-bar-button"
                onClick={handleSearch}
              >
                <i className="bi bi-search"></i>
              </button>
            </div>
          </div>

          <button
            className="ev-btn-lightblue-fill-round ev-btn-reg"
            onClick={() =>
              fetchData({ message: "Refreshing abstracts list..." })
            }
          >
            <i className="bi bi-arrow-repeat"></i> Refresh
          </button>
        </div>

        <div className="row gx-3 flex-grow-1 overflow-hidden  vh-60">
          <div className={filtersCollapsed ? "col-auto" : "ev-filter-panel-fixed"}>
            {/* vh-65, same as ScreeningView. At vh-75 this panel plus the
                header and pagination row overflowed the vh-80 card. */}
            <div className={filtersCollapsed ? "" : "vh-65 scrollable-element pe-1"}>

              {/* Filtering header */}
              <div
                className="ev-card-2 ev-radius-xsmall d-flex align-items-center justify-content-between p-1 mb-2 cursor-pointer gap-2 position-relative"
                  onClick={() => setFiltersCollapsed(prev => !prev)}
              >
                  {!filtersCollapsed && (
                    <>
                      {/* Absolutely centred so the title stays put whether or not
                          the Clear all button is showing. */}
                      <p className="ev-med-p-dark-blue ev-bold-med mb-0 position-absolute start-50 translate-middle-x">Filtering</p>
                      <div></div>
                    </>
                  )}
                  <i className={`bi ${filtersCollapsed ? "bi-funnel px-1" : "bi-chevron-left"} ev-med-p-dark-blue`}></i>
                  {filtersCollapsed && activeFilterCount > 0 && (
                    <span className="ev-message-card3 ev-bold-med ev-radius-large">
                      {activeFilterCount}
                    </span>
                  )}
              </div>

              {!filtersCollapsed && (
                <div className="d-flex flex-column gap-2">

                  {/* De-dup tab only — Match level + resolution status */}
                  {activeTab === "DEDUP" && (
                    <div className="ev-card-8 ev-radius-small p-2">
                      <div
                        className="d-flex align-items-center gap-2 cursor-pointer"
                        onClick={() => setDedupSectionOpen(p => !p)}
                      >
                        <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                          style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                          <i className="bi bi-files"></i>
                        </span>
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                          <p className="ev-small-p-dark ev-bold-med mb-0">Duplicates</p>
                          {(matchLevels.length > 0 || dedupStatus !== "ALL") && (
                            <span className="ev-message-card3 ev-btn-xsmall">
                              {matchLevels.length + (dedupStatus !== "ALL" ? 1 : 0)} selected
                            </span>
                          )}
                        </div>
                        <i className={`bi ${dedupSectionOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                      </div>

                      {dedupSectionOpen && (
                        <div className="mt-2">
                          <p className="ev-xsmall-p-dark ev-bold-med mb-1">Match Level</p>
                          <div className="d-flex gap-1 mb-2">
                            {MATCH_LEVEL_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`ev-btn-light-fill ev-btn-small flex-grow-1 ${matchLevels.includes(opt.value) ? "active" : ""}`}
                                onClick={() => toggleMatchLevel(opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          <p className="ev-xsmall-p-dark ev-bold-med mb-1">Status</p>
                          <div className="d-flex gap-1">
                            {DEDUP_STATUS_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`ev-btn-light-fill ev-btn-small flex-grow-1 ${dedupStatus === opt.value ? "active" : ""}`}
                                onClick={() => { setDedupStatus(opt.value); setPage(1); }}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SECTION 1 — Basic Filters */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection1Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        1
                      </span>
                      <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Basic Filters</p>
                      <i className={`bi ${section1Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                    </div>

                    {section1Open && (
                      <div className="mt-2">
                        {bibliographicIds.size > 0 && (
                          <>
                            <p className="ev-xsmall-p-dark ev-bold-med mb-1">Paper Type</p>
                            <div className="d-flex gap-2 mb-2">
                              {[
                                { value: "ALL", label: "All" },
                                { value: "BIBLIOGRAPHIC", label: "Bibliographic", icon: "bi-journal-text" },
                              ].map((opt) => (
                                <button
                                  key={opt.value}
                                  className={`ev-btn-light-fill ev-btn-reg flex-grow-1 ${paperType === opt.value ? "active" : ""}`}
                                  onClick={() => { setPaperType(opt.value); setPage(1); }}
                                >
                                  {opt.icon && <i className={`bi ${opt.icon} pe-1`} />}
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                        <p className="ev-xsmall-p-dark ev-bold-med mb-1">Publication Year</p>
                        <YearRangePicker
                          from={dateFrom}
                          to={dateTo}
                          onFromChange={(v) => { setDateFrom(v); setPage(1); }}
                          onToChange={(v) => { setDateTo(v); setPage(1); }}
                        />
              </div>
                    )}
                  </div>

                  {/* SECTION 2 — Tags */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection2Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        2
                      </span>
                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <p className="ev-small-p-dark ev-bold-med mb-0">Tags</p>
                        {selectedTagIds?.length > 0 && (
                          <span className="ev-message-card3 ev-btn-xsmall">
                            {selectedTagIds.length} selected
                          </span>
                        )}
                      </div>
                      <i className={`bi ${section2Open ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-dark`}></i>
                    </div>

                    {section2Open && (
                      <div className="mt-2">
              <TagFilterPanel
                tagOptions={effectiveTagOptions}
                selectedTagIds={selectedTagIds}
                onToggleTag={toggleTag}
                          onClearAll={() => setSelectedTagIds([])}
                          tagsHeight="vh-20"
              />
                      </div>
              )}
            </div>

                  {/* SECTION 3 — Advanced Filtering */}
                  <div className="ev-card-8 ev-radius-small p-2">
                    <div
                      className="d-flex align-items-center gap-2 cursor-pointer"
                      onClick={() => setSection3Open(p => !p)}
                    >
                      <span className="ev-message-card3 ev-bold-med d-flex align-items-center justify-content-center ev-radius-large flex-shrink-0"
                        style={{ width: 22, height: 22, fontSize: "var(--px-10)" }}>
                        3
                      </span>
                      <p className="ev-small-p-dark ev-bold-med mb-0 flex-grow-1">Advanced Filtering</p>
                      <div
                        className="form-check form-switch fs-6 mb-0 ms-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newVal = !advancedEnabled;
                          setAdvancedEnabled(newVal);
                          setSection3Open(newVal);
                        }}
                      >
                        <input
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          checked={advancedEnabled}
                          onChange={() => {}}
                        />
                      </div>
          </div>

                    {!section3Open && advancedEnabled && advConditions.some(c => c.category || c.value) && (
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span className="ev-small-p-dark">
                          {advConditions.filter(c => c.category || c.value).length} conditions applied
                        </span>
                      </div>
                    )}

                    {section3Open && (
                      <div className="mt-2">

                        {/* Unified filter rules */}
                        <div>
                          {advConditions.map((cond, idx) => (
                            <div key={cond.id}>
                              {idx > 0 && (
                                <div className="d-flex justify-content-center mb-2">
                                  <InlineSelect
                                    id={`${cond.id}:connector`}
                                    openId={advOpenDropdown}
                                    setOpenId={setAdvOpenDropdown}
                                    width={90}
                                    value={cond.connector || "AND"}
                                    placeholder="AND"
                                    options={["AND", "OR"].map((v) => ({
                                      value: v,
                                      label: v,
                                      onSelect: () => updateAdvCondition(cond.id, "connector", v),
                                    }))}
                                  />
                                </div>
                              )}
                              <div className="d-flex align-items-start gap-1 mb-2">
                                <InlineSelect
                                  id={`${cond.id}:field`}
                                  openId={advOpenDropdown}
                                  setOpenId={setAdvOpenDropdown}
                                  className="flex-grow-1"
                                  value={cond.category}
                                  placeholder="Field"
                                  options={ADV_FIELD_OPTIONS.map((o) => ({
                                    ...o,
                                    onSelect: () => updateAdvCondition(cond.id, "category", o.value),
                                  }))}
                                />
                                <InlineSelect
                                  id={`${cond.id}:operator`}
                                  openId={advOpenDropdown}
                                  setOpenId={setAdvOpenDropdown}
                                  className="flex-grow-1"
                                  value={cond.filterOp || "contains"}
                                  placeholder="Contains"
                                  options={ADV_OPERATOR_OPTIONS.map((o) => ({
                                    ...o,
                                    onSelect: () => updateAdvCondition(cond.id, "filterOp", o.value),
                                  }))}
                                />
                                {!["is_empty", "is_not_empty", "is_null", "is_not_null"].includes(cond.filterOp) && (
                                  <input
                                    type="text"
                                    className="formField4 ev-btn-small ev-radius-xsmall flex-grow-1"
                                    placeholder="Value"
                                    value={cond.value}
                                    onChange={(e) => updateAdvCondition(cond.id, "value", e.target.value)}
                                  />
                                )}
                                {advConditions.length > 1 && (
                                  <button
                                    className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0"
                                    onClick={() => removeAdvCondition(cond.id)}
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <button
                            className="ev-btn-text-only-fill ev-btn-small p-0"
                            onClick={addAdvCondition}
                          >
                            <i className="bi bi-plus-lg me-1"></i> Add rule
                          </button>
                        </div>

                        <div className="d-flex gap-1 mt-3">
                          <button
                            className="ev-btn-blue-outline-round ev-btn-reg ev-radius-small w-50"
                            onClick={() => setAdvConditions([{ id: 1, category: "", filterOp: "contains", connector: "AND", value: "" }])}
                          >
                            Clear
                          </button>
                          <button
                            className="ev-btn-blue-fill-round ev-btn-reg ev-radius-xsmall w-50"
                            onClick={() => fetchData({ message: "Applying filters..." })}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>

          <div className="col h-100 d-flex flex-column">
            {/* ── Metric card + tab toggle ── */}
            <div className="d-flex justify-content-between align-items-stretch gap-2 mb-2">
              {/* Metric card — left aligned, compact, inline format */}
              <div className="ev-card-8 d-flex align-items-center ev-radius-xsmall px-3 py-1 gap-0">
                <div className="d-flex align-items-center gap-1 pe-3">
                  <span className="ev-small-p-blue ev-bold-med">{totalCount}</span>
                  <span className="ev-xsmall-p-blue">Results</span>
                </div>
                {activeTab === "DEFAULT" && defaultData?.counts && (
                  <>
                    {defaultData.counts.included !== undefined && (
                      <>
                        <div className="vr mx-2"></div>
                        <div className="d-flex align-items-center gap-1 px-3">
                          <span className="ev-small-p-blue ev-bold-med text-success">{defaultData.counts.included ?? 0}</span>
                          <span className="ev-xsmall-p-blue text-success">Included</span>
                        </div>
                      </>
                    )}
                    {defaultData.counts.excluded !== undefined && (
                      <>
                        <div className="vr mx-2"></div>
                        <div className="d-flex align-items-center gap-1 px-3">
                          <span className="ev-small-p-blue ev-bold-med text-danger">{defaultData.counts.excluded ?? 0}</span>
                          <span className="ev-xsmall-p-blue text-danger">Excluded</span>
                        </div>
                      </>
                    )}
                    {defaultData.counts.pending !== undefined && (
                      <>
                        <div className="vr mx-2"></div>
                        <div className="d-flex align-items-center gap-1 px-3">
                          <span className="ev-small-p-blue ev-bold-med">{defaultData.counts.pending ?? 0}</span>
                          <span className="ev-xsmall-p-blue">Pending</span>
                        </div>
                      </>
                    )}
                  </>
                )}
                {activeTab === "DEDUP" && dedupData?.counts && (
                  <>
                    <div className="vr mx-2"></div>
                    <div className="d-flex align-items-center gap-1 px-3">
                      <span className="ev-small-p-blue ev-bold-med">{dedupData.counts.pending_groups ?? 0}</span>
                      <span className="ev-xsmall-p-blue">Pending</span>
                    </div>
                    <div className="vr mx-2"></div>
                    <div className="d-flex align-items-center gap-1 px-3">
                      <span className="ev-small-p-blue ev-bold-med text-success">{dedupData.counts.resolved_groups ?? 0}</span>
                      <span className="ev-xsmall-p-blue text-success">Resolved</span>
                    </div>
                  </>
                )}
              </div>

              {skippedPapersCount > 0 && (
                <button
                  type="button"
                  className="ev-btn-text-only-fill ev-fs-20 avatar-tooltip-bottom d-flex align-items-center me-auto ps-1"
                  data-tooltip={`${skippedPapersCount} paper${skippedPapersCount === 1 ? "" : "s"} skipped — click for details`}
                  onClick={() => setShowSkippedPapers(true)}
                >
                  <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                </button>
              )}

              {/* Tab toggle — right aligned */}
              <div className="ev-message-card6 d-flex gap-1 ev-radius-xsmall p-1 flex-shrink-0">
                <button
                  className={activeTab === "DEFAULT"
                    ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-reg"
                    : "ev-btn-lightblue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"}
                  onClick={() => { setActiveTab("DEFAULT"); setPage(1); }}
                >
                  Default Tab
                </button>
                <button
                  className={activeTab === "DEDUP"
                    ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-reg"
                    : "ev-btn-lightblue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"}
                  onClick={() => { setActiveTab("DEDUP"); setPage(1); }}
                >
                  De Duplication Tab
                </button>
              </div>
            </div>

            {activeTab === "DEDUP" && dedupLocked && (
              <div className="d-flex mb-2">
                <span className="ev-alert-card-warning ev-btn-small ev-radius-large ev-bold-med d-flex align-items-center">
                  <i className="bi bi-lock-fill me-1"></i>{DEDUP_LOCK_MESSAGE}
                </span>
              </div>
            )}

            {activeTab === "DEDUP" && !dedupLocked && (
              <div className="d-flex justify-content-start gap-2 mb-2">
                {showSaveSelection && (
                  <button
                    type="button"
                    className="ev-btn-yellow-fill-round ev-btn-small ev-bold-reg"
                    onClick={handleSaveSelection}
                  >
                    <i className="bi bi-check-circle me-1 ev-reg-p-dark"></i>{" "}
                    Save Selection
                  </button>
                )}

                <button
                  type="button"
                  className={`ev-btn-green-fill-round ev-btn-small d-flex align-items-center justify-content-center gap-2 px-2 pe-4 rounded-pill ${matchLevels.length ? "" : "opacity-50"}`}
                  style={matchLevels.length ? undefined : { cursor: "not-allowed" }}
                  disabled={!matchLevels.length}
                  title={matchLevels.length ? undefined : "Select a match level in the Duplicates filter to auto-resolve"}
                  onClick={() => setShowAutoResolveConfirm(true)}
                >
                  <i className="bi bi-check-circle me-1 ev-reg-p-light text-white"></i>{" "}
                  <span className="text-white">
                    {matchLevels.length
                      ? `Auto-resolve Groups (${matchLevelLabel} Matches)`
                      : "Auto-resolve Groups (select a match level)"}
                  </span>
                </button>
              </div>
            )}

            <div className="flex-grow-1 overflow-hidden">
              {!loading && (
                <>
                  {activeTab === "DEFAULT" && defaultData && (
                    <div className="d-flex flex-column gap-3 overflow-auto pe-2 scrollable-element vh-60 px-1">
                      {(defaultData?.abstracts || []).map((item) => {
                        const isSelected = selectedAbstractId === item.id;
                        const isExpanded = expandedAbstractIds.has(item.id);

                        return (
                          <div
                            key={item.id}
                            onClick={() => setSelectedAbstractId(item.id)}
                            className={`ev-card-8 w-100 ev-radius-small p-3 py-2 cursor-pointer transition-all ${isSelected ? "active" : ""}`}
                          >
                            <div className="d-flex gap-2 mb-2">
                              <span className="ev-card-2 px-3 py-1 ev-radius-xsmall ev-small-p-dark ev-btn-xsmall ev-bold-med">
                                ID. {item.study_id}
                              </span>
                              <span className="ev-card-2 px-3 py-1 ev-radius-xsmall ev-xsmall-p-dark ev-btn-xsmall ev-bold-med">
                                Year. {item.publication_year}
                              </span>
                              {bibliographicIds.has(String(item.study_id)) && (
                                <span className="ev-message-card3 px-3 py-1 ev-radius-xsmall ev-btn-xsmall ev-bold-med text-nowrap">
                                  <i className="bi bi-journal-text pe-1" />
                                  Bibliographic
                                </span>
                              )}
                            </div>

                            <p className="ev-bold-med ev-reg-p-dark mb-0">
                              {item.title}
                            </p>
                            <p className="ev-xsmall-p-dark mb-0 ev-bold-small">
                              <i>Authors. {item.author_names}</i>
                            </p>

                            <div className="mt-2 mb-2">
                              <button
                                type="button"
                                className="btn btn-link text-decoration-none p-0 ev-reg-p-blue ev-bold-med"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandedDefault(item.id);
                                }}
                              >
                                Abstract{" "}
                                <i
                                  className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"} ms-1`}
                                />
                              </button>

                              {isExpanded && (
                                <div className="ev-card-8 ev-radius-xsmall p-2 mt-2">
                                  <p className="ev-xsmall-p-dark mb-0">
                                    {(!item.abstract || item.abstract === "nan") ? "Not Available" : item.abstract}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {activeTab === "DEDUP" && dedupData && (
                    <div className="d-flex flex-column gap-2 pe-1 scrollable-element vh-55 px-1">
                      {(dedupData?.duplicate_groups || []).map((group) => {
                        const allItems = [
                          { ...group.original, _match_type: "ORIGINAL" },
                          ...(group.duplicates || []).map((d) => ({
                            ...d,
                            _match_type: "POTENTIAL",
                          })),
                        ];

                        // Unsaved selection, or the saved keepers
                        const selectedSet = getGroupSelection(group.group_key);
                        const isDirty = Boolean(selectedByGroup[group.group_key]);
                        const isResolved = group.status === "RESOLVED";

                        const selectedStudyIds = sortStudyIds(
                          allItems
                            .filter((x) => selectedSet.has(x.id))
                            .map((x) => String(x.study_id)),
                        );

                        let groupPill;
                        if (isDirty && needsReview(group.group_key)) {
                          groupPill = { label: "New paper — review", icon: "bi-exclamation-circle", cls: "ev-alert-card-warn" };
                        } else if (isDirty) {
                          groupPill = { label: "Unsaved changes", icon: "bi-pencil", cls: "ev-alert-card-warn" };
                        } else if (isResolved) {
                          groupPill = { label: "Resolved", icon: "bi-check2-circle", cls: "ev-alert-card-success" };
                        } else {
                          groupPill = { label: "Pending", icon: "bi-hourglass-split", cls: "ev-message-card3" };
                        }

                        return (
                          <div
                            key={group.group_key}
                            className="ev-card-2 p-2 ev-radius-small"
                          >
                            <div className="ev-radius-xsmall p-1 mb-2 d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <p className="ev-reg-p-dark ev-bold-med mb-0">
                                  {group.group_title}
                                </p>
                                {isResolved && !isDirty && (group.resolved_by_name || group.resolved_at) && (
                                  <p className="ev-xsmall-p-dark mb-0">
                                    Resolved
                                    {group.resolved_by_name ? ` by ${group.resolved_by_name}` : ""}
                                    {group.resolved_at ? ` · ${formatResolvedAt(group.resolved_at)}` : ""}
                                    {group.resolution_method
                                      ? ` · ${group.resolution_method === "AUTO" ? "Auto-resolved" : "Manual"}`
                                      : ""}
                                  </p>
                                )}
                              </div>

                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                {selectedStudyIds.length > 0 && (
                                  <div className="ev-message-card6 ev-btn-xsmall ev-radius-large px-3">
                                    Kept ID. {selectedStudyIds.join(", ")}
                                  </div>
                                )}
                                <div className={`${groupPill.cls} ev-radius-large ev-bold-med py-1 ev-btn-xsmall px-3 text-nowrap`}>
                                  <i className={`bi ${groupPill.icon} me-1`}></i>
                                  {groupPill.label}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex flex-column gap-2">
                              {allItems.map((item, itemIndex) => {
                                const isExpanded = expandedDedupItemIds.has(
                                  item.id,
                                );
                                const isChosen = selectedSet.has(item.id);

                                // Border: green = paper that stays, yellow = duplicate.
                                // No decision yet -> the first (original) paper is green.
                                // Once papers are selected / resolved -> kept papers are green.
                                const isKeptPaper = selectedSet.size > 0 ? isChosen : itemIndex === 0;
                                const paperBorderStyle = dedupCardBorderStyle(
                                  isKeptPaper ? DEDUP_CARD_COLORS.KEPT : DEDUP_CARD_COLORS.DUPLICATE,
                                );

                                // While editing: preview of what Save will do.
                                // Otherwise: the saved decision of this paper.
                                let paperTag = null;
                                let duplicateOf = [];
                                if (isDirty) {
                                  if (isChosen) paperTag = "KEPT";
                                  else if (selectedSet.size > 0) {
                                    paperTag = "DUPLICATE";
                                    duplicateOf = selectedStudyIds;
                                  }
                                } else if (item.dedup_status === "KEPT") {
                                  paperTag = "KEPT";
                                } else if (item.dedup_status === "DUPLICATE") {
                                  paperTag = "DUPLICATE";
                                  duplicateOf = sortStudyIds(item.duplicate_of || []);
                                }

                                return (
                                  <div
                                    key={item.id}
                                    className={`ev-card-4 p-2 ps-3 ev-radius-small ${isChosen ? "active" : ""}`}
                                    style={paperBorderStyle}
                                  >
                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                      <div className="d-flex flex-wrap gap-2">
                                        <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                          ID. {item.study_id}
                                        </span>
                                        <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                          Year. {item.publication_year}
                                        </span>
                                        {bibliographicIds.has(String(item.study_id)) && (
                                          <span className="ev-message-card3 px-2 py-1 ev-radius-xsmall ev-btn-xsmall ev-bold-med text-nowrap">
                                            <i className="bi bi-journal-text pe-1" />
                                            Bibliographic
                                          </span>
                                        )}
                                        {isValidField(item.journal_abbr) && (
                                          <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                            Journal:  {item.journal_abbr}
                                          </span>
                                        )}
                                        {isValidField(item.volume) && (
                                          <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                            Vol:     {item.volume}
                                          </span>
                                        )}
                                        {isValidField(item.issue) && (
                                          <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                            Issue:  {item.issue}
                                          </span>
                                        )}
                                        {isValidField(item.pages) && (
                                          <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                             Pages:  {item.pages}
                                          </span>
                                        )}
                                        {isValidField(item.doi) && (
                                          <span className="ev-card-2 px-2 py-1 ev-radius-xsmall ev-small-p-blue ev-btn-xsmall ev-bold-med">
                                             DOI: {item.doi}
                                          </span>
                                        )}
                                      </div>

                                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                        {paperTag === "KEPT" && (
                                          <div className="ev-alert-card-success ev-radius-large ev-bold-med py-1 ev-btn-xsmall px-3 text-nowrap">
                                            <i className="bi bi-check2 me-1"></i>Kept
                                          </div>
                                        )}
                                        {paperTag === "DUPLICATE" && (
                                          <div className="ev-alert-card-warn p-1 ev-radius-xsmall px-3 ev-fs-11">
                                            Duplicate{duplicateOf.length ? ` of ID ${duplicateOf.join(", ")}` : ""}
                                          </div>
                                        )}
                                        <MatchPill type={item._match_type} level={item.duplicate_level} />

                                        <button
                                          type="button"
                                          className={`ev-btn-blue-outline-round ev-btn-xsmall ev-bold-med ${dedupLocked ? "opacity-50" : ""}`}
                                          style={dedupLocked ? { cursor: "not-allowed" } : undefined}
                                          title={dedupLocked ? DEDUP_LOCK_MESSAGE : undefined}
                                          disabled={dedupLocked}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectDedupAbstract(
                                              group.group_key,
                                              item.id,
                                            );
                                          }}
                                        >
                                          {isChosen
                                            ? "Selected Paper"
                                            : "✓ Keep This Paper"}
                                        </button>
                                      </div>
                                    </div>

                                    <p className="ev-bold-med ev-reg-p-dark mb-0">
                                      {item.title}
                                    </p>
                                    <p className="ev-xsmall-p-dark mb-0 ev-bold-small">
                                      <i>Authors. {item.author_names}</i>
                                    </p>

                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="btn btn-link text-decoration-none p-0 ev-reg-p-blue ev-bold-med"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleExpandedDedup(item.id);
                                        }}
                                      >
                                        Abstract{" "}
                                        <i
                                          className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"} ms-1`}
                                        />
                                      </button>

                                      {isExpanded && (
                                        <div className="ev-card-8 ev-radius-xsmall p-2 mt-2">
                                          <p className="ev-xsmall-p-dark mb-0">
                                            {(!item.abstract || item.abstract === "nan") ? "Not Available" : item.abstract}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {!loading && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="ev-xsmall-p-dark">
                    Showing {showingCount} of {totalCount} Results
                  </span>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="ev-btn-light-fill ev-btn-xsmall"
                      disabled={page === 1}
                      onClick={handlePrev}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>

                    <button
                      className="ev-btn-light-fill ev-btn-xsmall"
                      disabled
                    >
                      Page {page} of {totalPages || 1}
                    </button>

                    <button
                      className="ev-btn-light-fill ev-btn-xsmall"
                      disabled={page >= totalPages}
                      onClick={handleNext}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>

                  <div className="text-end">
                    <PaginationControls
                      initialSize={pageSize}
                      onSizeChange={(newSize) => {
                        setPageSize(newSize);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}

        {isErrorPopupVisible && (
          <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
        )}

        {isSuccessPopupVisible && (
          <SuccessPopup
            onClose={closeSuccessPopup}
            message={popupSuccessMessage}
          />
        )}
        {/* Confirmation Modals */}
        {showAutoResolveConfirm && (
          <ConfirmationModal
            show={showAutoResolveConfirm}
            title="Auto-resolve duplicates?"
            message={`Every pending duplicate group that matches the current filters and has a ${matchLevelLabel || "selected"} match will be resolved as a whole: the original paper (or the papers already kept) stays, and all other papers in the group are marked EXCLUDED.`}
            confirmButtonText="Yes, Auto-resolve"
            cancelButtonText="Cancel"
            onConfirm={confirmAutoResolveExact}
            onClose={() => setShowAutoResolveConfirm(false)}
          />
        )}

        {showSaveConfirm && (
          <ConfirmationModal  
            show={showSaveConfirm}
            title="Confirm Save Selection?"
            message={`Selected papers will be KEPT and all other papers in ${pendingSavePayload?.length === 1 ? "this group" : `these ${pendingSavePayload?.length || 0} groups`} will be EXCLUDED.`}
            confirmButtonText="Yes, Save"
            cancelButtonText="Cancel"
            onConfirm={confirmSaveSelection}
            onClose={() => setShowSaveConfirm(false)}
          />
        )}

        {showSkippedPapers && (
          <ConfirmationModal
            show={showSkippedPapers}
            title="Papers skipped during migration"
            message=""
            showCancel={false}
            confirmButtonText="Close"
            onConfirm={() => setShowSkippedPapers(false)}
            onClose={() => setShowSkippedPapers(false)}
            bodyContent={
              <>
                <p className="ev-xsmall-p-dark mb-2">
                  These papers had no title in Discovery, so they could not be
                  imported into this project.
                </p>
                <div
                  className="scrollable-element pe-1"
                  style={{ maxHeight: "55vh", overflowY: "auto" }}
                >
                  {migrationWarnings.map((w) => (
                    <div key={w.discovery_id} className="mb-3">
                      <div className="ev-bold-med ev-small-p-blue mb-2 d-flex align-items-center gap-2">
                        <i className="bi bi-search flex-shrink-0"></i>
                        <span className="text-break">
                          {previewSearchQuery(w.search_query) ||
                            `Search #${w.discovery_id}`}
                        </span>
                        <span className="ev-message-card3 ev-btn-xsmall flex-shrink-0">
                          {w.skipped_count}
                        </span>
                      </div>

                      {(w.skipped || []).map((p) => (
                        <div
                          key={p.discovery_result_id}
                          className="ev-card-2 ev-radius-xsmall p-2 mb-2"
                        >
                          <p className="ev-small-p-dark mb-1 text-break">
                            <span className="ev-bold-med">Author(s): </span>
                            <i>{p.authors || "Not available"}</i>
                          </p>
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            {p.publication_year && (
                              <span className="ev-message-card3 ev-btn-xsmall">
                                {p.publication_year}
                              </span>
                            )}
                            {p.pmid && (
                              <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                                PMID {p.pmid}
                              </span>
                            )}
                            {p.doi && (
                              <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall text-break">
                                DOI {p.doi}
                              </span>
                            )}
                            {!p.pmid && !p.doi && (
                              <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">
                                Discovery record {p.discovery_result_id}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            }
          />
        )}
      </div>
    );
};

export default AbstractsList;